<template>
  <div>
    <v-row>
      <v-col cols="12">
        <app-tile-group
          :model-value="envelopeTypeFormField.$model"
          :options="envelopeTypeTileOptions"
          :disabled="isEditingLimited"
          :aria-label="$t('project.signing-checklist.add.step2.Section3.envelopeTypeLabel')"
          data-locator="envelope-type-tile-group"
          @update:model-value="envelopeTypeChange"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import { createNamespacedHelpers } from 'vuex'

import { formFieldErrors, VuelidateFormField } from '@/common/utils/form'
import type { TileOption } from '@/design-system/tiles/types'
import { ENVELOPE_TYPES, SIGNATURE_TYPE_DOCUSIGN_QUALIFIED } from '@/project/signing-checklist/constants'
import { SET_IMPORTED_FILE_ENVELOPE_TYPE } from '@/store/modules/signing-checklist/mutation_types'

type EnvelopeTypeOption = {
  value: string
  disabled: boolean
}

type DataType = {
  envelopeTypeOptions: EnvelopeTypeOption[]
}

const { mapMutations: mapSigningChecklistMutations } = createNamespacedHelpers('signingChecklist')

export default defineComponent({
  name: 'Section3',
  props: {
    fileId: {
      type: Number,
      required: true,
    },
    isEditingLimited: {
      type: Boolean,
      required: true,
    },
    isMultiEnvelopeChild: {
      type: Boolean,
      required: true,
    },
    envelopeTypeFormField: {
      type: Object as PropType<VuelidateFormField>,
      required: true,
    },
    signatureType: {
      type: String,
      required: true,
    },
  },
  data (): DataType {
    return {
      envelopeTypeOptions: [],
    }
  },
  computed: {
    envelopeTypeErrorMessages (): Array<string> {
      return formFieldErrors(
        this.envelopeTypeFormField,
        [
          ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('project.signing-checklist.add.step2.Section3.envelopeTypeLabel') })],
        ],
      )
    },
    envelopeTypeTileOptions (): TileOption[] {
      return this.envelopeTypeOptions.map(option => ({
        value: option.value,
        label: this.getTileLabel(option),
        caption: this.$t(`project.signing-checklist.add.step2.Section3.envelopeTypeOptions.${option.value.toLowerCase()}Info`),
        disabled: option.disabled,
        dataLocator: `${option.value}-envelope-type-card`,
      }))
    },
  },
  methods: {
    ...mapSigningChecklistMutations([SET_IMPORTED_FILE_ENVELOPE_TYPE]),
    envelopeTypeChange (value: string | null) {
      this[SET_IMPORTED_FILE_ENVELOPE_TYPE]({
        fileId: this.fileId,
        value,
      })
    },
    getTileLabel (option: EnvelopeTypeOption): string {
      const label = this.$t(`project.signing-checklist.add.step2.Section3.envelopeTypeOptions.${option.value.toLowerCase()}`)
      if (option.value === ENVELOPE_TYPES.SINGLE && this.isMultiEnvelopeChild) {
        return `${label} (${this.$t('common.envelopeType.fromBulk')})`
      }
      return label
    },
    setEnvelopeTypeOptions (): void {
      Object.values(ENVELOPE_TYPES).forEach(value => {
        const isDisabled = value === ENVELOPE_TYPES.MULTIPLE.toUpperCase() && this.signatureType === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED
        if (value !== ENVELOPE_TYPES.NDA) {
          this.envelopeTypeOptions.push({ value: value, disabled: isDisabled })
        }
      })
    },
  },
  mounted () {
    this.setEnvelopeTypeOptions()
  },
  watch: {
    signatureType (newValue) {
      this.envelopeTypeOptions = this.envelopeTypeOptions.map(env =>
        env.value === ENVELOPE_TYPES.MULTIPLE ? { ...env, disabled: newValue === SIGNATURE_TYPE_DOCUSIGN_QUALIFIED } : env,
      )
    },
  },
})
</script>
